import { OrganizationCodes } from '@revenuemarket/bank';
import QueryString from 'qs';

import { API } from '~apis/axios/instance';
import { CancelType, HomeForeignType, PaymentType } from '~apis/card';
import { CashReceiptTransactionType } from '~apis/cash-receipt';
import { CASH_RECEIPT_TYPE } from '~apis/scrape';
import { TAX_INVOICE_TYPE } from '~apis/tax-invoice';
import { SettlementStatus } from '~components/Settlement/utils';
import { AccountType } from '~models/bank/enum';
import { TaxInvoiceType } from '~models/tax-invoice';
import { Page, ResponseBaseWithData } from '~types/response';

export interface TaxInvoiceMappedLabelsDto {
  taxInvoiceId?: string;
  taxInvoiceType?: TaxInvoiceType;
  transactionAt?: string;
  transactionName?: string;
  transactionDescription?: string;
  transactionLabelAmountKrw?: number;
}

export interface InvoiceMapping {
  taxInvoiceId: string;
  taxInvoiceType: TAX_INVOICE_TYPE;
  reportingDate: string;
  counterPartyName: string;
  counterPartyCompanyName: string;
  businessEntityId: number;
  repItems: string;
  totalAmount: number;
  mappingAmount: number;
  unmappedAmount: number;
  status: SettlementStatus;
  expectedPaymentDate: string;
  actualPaymentDate: string;
  mappedCount: number;
  mappedLabels: TaxInvoiceMappedLabelsDto[];
  counterPartyBusinessRegNumber: string;
  contractorName: string;
  contractorRegNumber: string;
  contractorCompanyName: string;
  supplierName: string;
  supplierRegNumber: string;
  supplierCompanyName: string;
}

export interface InvoiceMappingRequest {
  startDate: string;
  endDate: string;
  status?: SettlementStatus;
  type: TAX_INVOICE_TYPE;
  page?: number;
  size?: number;
  sort?: string[];
  businessEntityIds?: number[];
  searchParam?: string;
}

export const getInvoiceMappings = ({
  companyId,
  requestParams,
}: {
  companyId: string;
  requestParams: InvoiceMappingRequest;
}) => {
  const params = QueryString.stringify(requestParams, { arrayFormat: 'repeat' });
  return API.get<ResponseBaseWithData<Page<InvoiceMapping>>>(
    `/api/v3/companies/${companyId}/invoice-mappings?${params}`,
  );
};

export const getDownloadInvoiceMappings = ({
  companyId,
  requestParams,
}: {
  companyId: string;
  requestParams: InvoiceMappingRequest;
}) => {
  const params = QueryString.stringify(requestParams, { arrayFormat: 'repeat' });
  return API.get(
    `/api/v3/companies/${companyId}/invoice-mappings/download?${params}`,
    {
      responseType: 'blob',
    },
    true,
  );
};

export interface PurchaseCashReceiptMapping {
  cashReceiptId: string;
  transactionType: CashReceiptTransactionType;
  usedDateTime: string;
  totalAmount: number;
  supplyAmount: number;
  vatAmount: number;
  tipAmount: number;
  isMapped: boolean;
  mappedCount: number;
  mappedLabelsCount: number;
  userName: string | null;
  supplierCompanyName: string | null;
  supplierBusinessRegNo: string | null;
  deductType: string | null;
}

export interface SalesCashReceiptMapping {
  cashReceiptId: string;
  transactionType: CashReceiptTransactionType;
  usedDateTime: string;
  totalAmount: number;
  supplyAmount: number;
  vatAmount: number;
  tipAmount: number;
  isMapped: boolean;
  mappedCount: number;
  mappedLabelsCount: number;
  companyName: string | null;
  companyBusinessRegNo: string | null;
  issueType: string | null;
  idMeans: string | null;
}

export interface PurchaseCashReceiptMappingRequest {
  startDate: string;
  endDate: string;
  baseAmount?: number;
  page?: number;
  size?: number;
  sort?: string[];
  searchParam?: string;
}

export interface SalesCashReceiptMappingRequest {
  startDate: string;
  endDate: string;
  baseAmount?: number;
  page?: number;
  size?: number;
  sort?: string[];
  searchParam?: string;
}

export const getPurchaseCashReceiptMappings = ({
  companyId,
  requestParams,
}: {
  companyId: string;
  requestParams: PurchaseCashReceiptMappingRequest;
}) => {
  const params = QueryString.stringify(requestParams, { arrayFormat: 'repeat' });
  return API.get<ResponseBaseWithData<Page<PurchaseCashReceiptMapping>>>(
    `/api/v3/companies/${companyId}/purchase-cashreceipt-mappings?${params}`,
  );
};

export const getSalesCashReceiptMappings = ({
  companyId,
  requestParams,
}: {
  companyId: string;
  requestParams: SalesCashReceiptMappingRequest;
}) => {
  const params = QueryString.stringify(requestParams, { arrayFormat: 'repeat' });
  return API.get<ResponseBaseWithData<Page<SalesCashReceiptMapping>>>(
    `/api/v3/companies/${companyId}/sales-cashreceipt-mappings?${params}`,
  );
};

export interface CorporateCardApprovalMapping {
  id: number;
  approvalNo: string;
  organizationCode: OrganizationCodes;
  organizationName: string;
  companyId: string;
  cardNo: string;
  currency: string;
  usedAmount: number;
  usedKrwAmount: number;
  canceledAmount: number;
  cancelType: CancelType;
  homeForeignType: HomeForeignType;
  paymentType: PaymentType;
  vat: number;
  installmentMonth: number;
  memberStoreName: string;
  memberStoreCorpNo: string;
  usedAt: string;
  userNames: string[];
  createdAt: string;
  updatedAt: string;

  isMapped: boolean;
}

export interface CorporateCardApprovalMappingRequest {
  startDate: string;
  endDate: string;
  searchParam?: string;
  mappingAmount?: number;
  cardNos?: string[];
  page?: number;
  size?: number;
  sort?: string[];
}

export const getCorporateCardApprovalMappings = ({
  companyId,
  requestParams,
}: {
  companyId: string;
  requestParams: CorporateCardApprovalMappingRequest;
}) => {
  const params = QueryString.stringify(requestParams, { arrayFormat: 'repeat' });
  return API.get<ResponseBaseWithData<Page<CorporateCardApprovalMapping>>>(
    `/api/v3/companies/${companyId}/card-approval-mappings?${params}`,
  );
};

export const updateExpectedPaymentDate = ({
  companyId,
  taxInvoiceId,
  expectedPaymentDate,
  type,
}: {
  companyId: string;
  taxInvoiceId: string;
  expectedPaymentDate: string | null;
  type: TAX_INVOICE_TYPE;
}) => {
  return API.patch(
    `/api/v3/companies/${companyId}/invoice-mappings/${taxInvoiceId}/expected-payment-date?type=${type}&expectedPaymentDate=${expectedPaymentDate}`,
  );
};

export interface TransactionLabelMappings {
  transactionId: number;
  transactionAt: string;
  transactionName: string;
  transactionDescription: string;
  transactionAmount: number;
  accountType: AccountType;
  accountId: number;
  labelMappings: IMappedInvoicesItem[];
}
export interface IMappedInvoicesItem {
  labelId: number | null;
  categoryLabelKey: string | null;
  businessEntityLabelId: number | null;
  groupLabelId: number | null;
  mappingLabel: string;
  transactionLabelAmount: number;
  mappedInvoice: IMappedInvoice;
  repItems: string;
  businessEntityName: string | null;
}
export interface IMappedInvoice {
  labelId: number;
  taxInvoiceId: string;
  taxInvoiceType: TaxInvoiceType;
  reportingDate: string;
  repItems: string;
  counterPartyName: string;
  counterPartyCompanyName: string;
  counterPartyBusinessRegNumber: string;
  totalAmount: number;
  mappingAmount: number;
  unmappedAmount: number;
  createdAt: string;
  updatedAt: string;
}

export const getTransactionLabelMappings = ({
  companyId,
  transactionId,
}: {
  companyId: string;
  transactionId: number;
}) => {
  return API.get<ResponseBaseWithData<TransactionLabelMappings>>(
    `/api/v3/companies/${companyId}/transaction-label-mappings/${transactionId}`,
  );
};

export interface TaxInvoiceMapping {
  taxInvoiceId: string;
  taxInvoiceType: TaxInvoiceType;
  reportingDate: string;
  counterPartyCompanyName: string;
  counterPartyName: string;
  counterPartyBusinessRegNumber: string;
  totalAmount: number;
  repItems: string;
  mappedTransactionLabels: IMappedTransactionLabelsItem[];
  mappedInvoiceAdjustments: IMappedInvoiceAdjustmentsItem[];
}
export interface IMappedTransactionLabelsItem {
  labelId: number;
  categoryLabelKey: string;
  categoryId: number | null;
  companyDetailedCategoryId: number | null;
  groupLabelId: number;
  transactionId: number;
  transactionName: string;
  transactionDescription: string;
  transactionAmount: number;
  transactionAmountKrw: number;
  transactionLabelAmount: number;
  transactionLabelAmountKrw: number;
  transactionAt: string;
}
export interface IMappedInvoiceAdjustmentsItem {
  adjustmentId: number;
  reportingDate: string;
  counterPartyCompanyName: string;
  counterPartyBusinessRegNumber: string;
  adjustmentAmount: number;
  memo: string;
}

export const getTaxInvoiceMappings = ({
  companyId,
  taxInvoiceId,
  type,
}: {
  companyId: string;
  taxInvoiceId: string;
  type: TaxInvoiceType;
}) => {
  return API.get<ResponseBaseWithData<TaxInvoiceMapping>>(
    `/api/v3/companies/${companyId}/invoice-mappings/${taxInvoiceId}?type=${type}`,
  );
};

export interface mappingTransactionRequest {
  companyId: string;
  type: TaxInvoiceType;
  taxInvoiceId: string;
  data: mappingTransactionPayload;
}
export interface mappingTransactionPayload {
  transactionLabels: ITransactionLabelsItem[];
  invoiceAdjustments: IInvoiceAdjustmentsItem[];
}
interface ITransactionLabelsItem {
  labelId: number;
  transactionId: number;
  categoryId: number;
  companyDetailedCategoryId: number | null;
  customLabelId: number | null;
  mappingAmount: number;
}
interface IInvoiceAdjustmentsItem {
  adjustmentAmount: number;
  reportingDate: string;
  memo: string;
}

export const mappingTransactionToTaxInvoice = ({
  companyId,
  type,
  taxInvoiceId,
  data,
}: {
  companyId: string;
  type: TaxInvoiceType;
  taxInvoiceId: string;
  data: mappingTransactionPayload;
}) => {
  return API.put(`/api/v3/companies/${companyId}/invoice-mappings/${taxInvoiceId}/${type}`, {
    data,
  });
};

export interface MappingTaxInvoiceAndSplitToTransactionRequest {
  companyId: string;
  transactionId: number;
  data: MappingLables;
}

export interface MappingLables {
  labels: ILabelsItem[];
}
interface ILabelsItem {
  amount: number;
  categoryId: number;
  customLabelId: number | null;
  companyDetailedCategoryId: number | null;
  businessEntityId: number | null;
  invoiceId: string | null;
  invoiceType: TaxInvoiceType | null;
}

export const mappingTaxInvoiceAndSplitToTransaction = ({
  companyId,
  transactionId,
  data,
}: MappingTaxInvoiceAndSplitToTransactionRequest) => {
  return API.put(`/api/v3/companies/${companyId}/transaction-label-mappings/${transactionId}`, {
    data,
  });
};

// 체크카드 승인내역 매핑
export interface MappingCheckCardApprovalsToTransactionRequest {
  companyId: string;
  transactionId: number;
  data: {
    labels: CheckCardApprovalLabelItem[];
  };
}

interface CheckCardApprovalLabelItem {
  amount: number;
  categoryId: number;
  customLabelId: number | null;
  companyDetailedCategoryId: number | null;
  approvalId: number | null;
}

export const mappingCheckCardApprovalsToTransaction = ({
  companyId,
  transactionId,
  data,
}: MappingCheckCardApprovalsToTransactionRequest) => {
  return API.put(
    `/api/v3/companies/${companyId}/transaction-label-mappings/${transactionId}/check-card-approvals`,
    {
      data,
    },
  );
};

// 현금영수증 매핑
export interface MappingCashReceiptsToTransactionRequest {
  companyId: string;
  transactionId: number;
  data: {
    labels: CashReceiptLabelItem[];
  };
}

interface CashReceiptLabelItem {
  amount: number;
  categoryId: number;
  customLabelId: number | null;
  companyDetailedCategoryId: number | null;
  cashReceiptId: string | null;
  cashReceiptType: CASH_RECEIPT_TYPE | null;
  businessEntityId: number | null;
}

export const mappingCashReceiptsToTransaction = ({
  companyId,
  transactionId,
  data,
}: MappingCashReceiptsToTransactionRequest) => {
  return API.put(
    `/api/v3/companies/${companyId}/transaction-label-mappings/${transactionId}/cash-receipts`,
    {
      data,
    },
  );
};

export interface CancelMappingForTaxInvoiceRequest {
  taxInvoiceKeys: ITaxInvoiceKeysItem[];
}
interface ITaxInvoiceKeysItem {
  taxInvoiceId: string;
  taxInvoiceType: string;
}

export const cancelMappingForTaxInvoice = ({
  companyId,
  request,
}: {
  companyId: string;
  request: CancelMappingForTaxInvoiceRequest;
}) => {
  return API.delete(`/api/v3/companies/${companyId}/invoice-mappings/bulk`, {
    data: request,
  });
};

export interface TaxInvoiceIntoLabelItemRequest {
  companyId: string;
  transactionId: number;
  labelId: number;
  mappingAmount: number;
  invoiceId: string;
  invoiceType: TaxInvoiceType;
  invoiceAmount: number;
}

export const mapTaxInvoiceIntoLabelItem = ({
  companyId,
  transactionId,
  labelId,
  mappingAmount,
  invoiceId,
  invoiceType,
  invoiceAmount,
}: TaxInvoiceIntoLabelItemRequest) => {
  return API.put(
    `/api/v3/companies/${companyId}/transaction-label-mappings/${transactionId}/transaction-labels/${labelId}`,
    {
      data: {
        transactionId,
        labelId,
        mappingAmount,
        invoiceId,
        invoiceType,
        invoiceAmount,
      },
    },
  );
};
